'use client';

import { LifeCover } from '@app/domain/model/eshop/life/LifeCover';
import { useCallback, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { Coverage } from '@app/domain/model/Coverage';
import { SelectedCover } from '@app/domain/model/eshop/life/SelectedCover';
import { useCoverConfigs } from '@app/app/esales/life/_sections/steps/CustomCoverSelectionStep/useCoverConfigs';
import { useOpenCoverConfig } from '@app/app/esales/life/_sections/steps/CustomCoverSelectionStep/useOpenCoverConfig';
import { CoverDetailsView } from '@app/app/esales/life/_sections/steps/CoverDetailsView';
import { SessionResponse } from '@app/domain/model/eshop/life/session/SessionResponse';
import { LifePackageCode } from '@app/domain/model/eshop/life/LifePackageCode';
import { CoverConfigItem } from '@app/app/esales/life/_sections/steps/CustomCoverSelectionStep/CoverConfigItem';
import { useChangeEffect } from '@app/lib/use-change-effect/useChangeEffect';
import { getATDBasedMinimums } from '@app/app/esales/life/_sections/steps/CustomCoverSelectionStep/getATDBasedMinimums';

interface Props {
    covers: LifeCover[];
    onCoverChange: () => void;
    sessionData: Nullable<SessionResponse>;
}

function initializeSelectedCovers(covers: LifeCover[], sessionData: Nullable<SessionResponse>): SelectedCover[] {
    const sessionCovers = sessionData?.firstStep?.covers;
    const isFreePlan = sessionData?.firstStep?.selectedPackage === LifePackageCode.Free;

    const initialCovers: SelectedCover[] = covers.map(cover => {
        const sessionCover = isFreePlan
            ? sessionCovers?.find(sessionCover => sessionCover.code === cover.code)
            : undefined;

        if (sessionCover) {
            return {
                code: cover.code,
                enabled: true,
                value: sessionCover.sum ?? cover.default,
            };
        }

        return {
            code: cover.code,
            enabled: cover.preselected,
            value: cover.default,
        };
    });

    return initialCovers;
}

export function CustomCoverSelectionStep({ covers, onCoverChange, sessionData }: Props) {
    const debouncedCoverChange = useDebouncedCallback(() => {
        void onCoverChange();
    }, 500);

    const [selectedCovers, setSelectedCovers] = useState<SelectedCover[]>(() =>
        initializeSelectedCovers(covers, sessionData),
    );

    const [openCoverCode, setOpenCoverCode] = useState<Nullable<string>>(null);

    const handleSwitchChange = useCallback(
        (coverCode: string, checked: boolean) => {
            setSelectedCovers(prev => {
                let updatedCovers = prev.map(selectedCover =>
                    selectedCover.code === coverCode ? { ...selectedCover, enabled: checked } : selectedCover,
                );

                if (
                    (coverCode === Coverage.TemporaryDisabilityByAccident || coverCode === Coverage.InjuryByAccident) &&
                    checked
                ) {
                    updatedCovers = updatedCovers.map(selectedCover =>
                        selectedCover.code === Coverage.DisabilityByAccident
                            ? { ...selectedCover, enabled: true }
                            : selectedCover,
                    );
                }

                if (coverCode === Coverage.DisabilityByAccident && !checked) {
                    updatedCovers = updatedCovers.map(selectedCover =>
                        selectedCover.code === Coverage.InjuryByAccident ||
                        selectedCover.code === Coverage.TemporaryDisabilityByAccident
                            ? { ...selectedCover, enabled: false }
                            : selectedCover,
                    );
                }

                return updatedCovers;
            });
            debouncedCoverChange();
        },
        [debouncedCoverChange],
    );

    const handleValueChange = useCallback(
        (coverCode: string, value: number) => {
            setSelectedCovers(prev =>
                prev.map(selectedCover =>
                    selectedCover.code === coverCode ? { ...selectedCover, value } : selectedCover,
                ),
            );
            debouncedCoverChange();
        },
        [debouncedCoverChange],
    );

    const atdCover = selectedCovers.find(sc => sc.code === Coverage.TemporaryDisabilityByAccident);
    const atdEnabled = atdCover?.enabled ?? false;
    const atdValue = atdCover?.value ?? 0;

    useChangeEffect(() => {
        if (!atdEnabled) {
            return;
        }

        const minimums = getATDBasedMinimums(atdValue);

        setSelectedCovers(prev => {
            let hasChanges = false;
            const updated = prev.map(selectedCover => {
                if (selectedCover.code === Coverage.LifeInsurance && selectedCover.value < minimums.diMin) {
                    hasChanges = true;
                    return { ...selectedCover, value: minimums.diMin };
                }
                if (selectedCover.code === Coverage.DisabilityByAccident && selectedCover.value < minimums.apdMin) {
                    hasChanges = true;
                    return { ...selectedCover, value: minimums.apdMin };
                }
                return selectedCover;
            });

            if (hasChanges) {
                debouncedCoverChange();
            }

            return hasChanges ? updated : prev;
        });
    }, `${atdEnabled}-${atdValue}`);

    const handleReadMoreClick = useCallback((coverCode: string) => {
        setOpenCoverCode(coverCode);
    }, []);

    const handleCloseCoverDetails = useCallback(() => {
        setOpenCoverCode(null);
    }, []);

    const coverConfigs = useCoverConfigs(covers, selectedCovers);

    const openCoverConfig = useOpenCoverConfig(openCoverCode, covers, selectedCovers);

    const handleDetailsValueChange = useCallback(
        (value: number) => {
            if (openCoverCode) {
                handleValueChange(openCoverCode, value);
            }
        },
        [openCoverCode, handleValueChange],
    );

    const handleDetailsSwitchChange = useCallback(
        (checked: boolean) => {
            if (openCoverCode) {
                handleSwitchChange(openCoverCode, checked);
            }
        },
        [openCoverCode, handleSwitchChange],
    );

    return (
        <div className="flex flex-col gap-4">
            {coverConfigs.map(
                ({ cover, selectedCover, min, max, step, isSwitchDisabled, canUseChoiceButtons, options }) => (
                    <CoverConfigItem
                        key={cover.code}
                        cover={cover}
                        selectedCover={selectedCover}
                        min={min}
                        max={max}
                        step={step}
                        isSwitchDisabled={isSwitchDisabled}
                        canUseChoiceButtons={canUseChoiceButtons}
                        options={options}
                        onSwitchChange={handleSwitchChange}
                        onValueChange={handleValueChange}
                        onReadMoreClick={handleReadMoreClick}
                    />
                ),
            )}

            <CoverDetailsView
                selectedCover={openCoverConfig?.cover ?? null}
                selectedCoverPackageSum={openCoverConfig?.selectedCover?.value}
                selectedCoverEnabled={openCoverConfig?.selectedCover?.enabled ?? false}
                open={openCoverCode !== null}
                onBack={handleCloseCoverDetails}
                onClose={handleCloseCoverDetails}
                showRangeSlider={!openCoverConfig?.canUseChoiceButtons}
                showChoiceButtons={openCoverConfig?.canUseChoiceButtons ?? false}
                choiceOptions={openCoverConfig?.options ?? []}
                onValueChange={handleDetailsValueChange}
                onSwitchChange={handleDetailsSwitchChange}
                coverMin={openCoverConfig?.min ?? 0}
                coverMax={openCoverConfig?.max ?? 0}
                coverStep={openCoverConfig?.step ?? 1}
                coverDefault={openCoverConfig?.default ?? 0}
            />
        </div>
    );
}
